import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { FormRegisterChangeEmailData } from 'dashboard/forms/FormRegisterChangeEmail';
import SessionClientOnboard from 'dashboard/lib/clients/SessionClientOnboard';
import useRedirectIfLoggedIn from 'dashboard/lib/hooks/useRedirectIfLoggedIn';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { OnboardingFlowV1 } from 'dashboard/models/onboarding-flow-v1';
import { showMessage } from 'dashboard/redux/actions/messages';
import { saveOnboardingFlow } from 'dashboard/redux/actions/onboarding-flow';
import { RootState } from 'dashboard/redux/store';
import DashboardRegisterConfirmView from 'dashboard/views/DashboardRegisterConfirmView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { ApiModel } from 'lib/interfaces/api';

const DashboardRegisterConfirmRoute = (props: PropsFromRedux) => {
  useRedirectIfLoggedIn();

  const navigate = useNavigate();

  useEffect(() => {
    if (
      props.onboardingFlow == null ||
      props.onboardingFlow?.attributes?.['email-address'] == null
    ) {
      navigate('/dashboard/register', { replace: true });
      return;
    }

    // Send activation instructions immediately otherwise
    sendActivationInstructions();
  }, []);

  // For generating activation token and sending activation email
  const { error, callback: sendActivationInstructions } = useApiCallback(async () => {
    await SessionClientOnboard.postResourceAction(
      props.onboardingFlow,
      'send-activation-instructions',
    );
  }, [props.onboardingFlow]);

  useApiErrorHandler({
    error: error as Error,
    onError: () => props.showMessage('An error occurred. Please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('An error occurred. Please try again.', MessageType.Failure),
  });

  // For resending activation email only
  const { error: resendError, callback: resendEmail } = useApiCallback(async () => {
    await SessionClientOnboard.postResourceAction(
      props.onboardingFlow,
      'send-activation-instructions',
    );

    props.showMessage(
      'Resent activation instructions. Please check your email.',
      MessageType.Success,
    );
  }, [props.onboardingFlow, props.showMessage]);

  useApiErrorHandler({
    error: resendError as Error,
    onError: () => props.showMessage('An error occurred. Please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('An error occurred. Please try again.', MessageType.Failure),
  });

  // For changing email address and resending activation email
  const { error: updateError, callback: updateEmail } = useApiCallback(
    async ({ 'email-address': emailAddress }: FormRegisterChangeEmailData) => {
      const onboardingFlow = await SessionClientOnboard.updateResource<OnboardingFlowV1>({
        id: props.onboardingFlow.id,
        type: ApiModel.OnboardingFlowV1,
        attributes: {
          'email-address': emailAddress,
        },
      });

      props.saveOnboardingFlow(onboardingFlow.data);
      await SessionClientOnboard.postResourceAction(
        onboardingFlow.data,
        'send-activation-instructions',
      );

      props.showMessage(
        'Resent activation instructions. Please check your email.',
        MessageType.Success,
      );
    },
    [props.onboardingFlow, props.saveOnboardingFlow, props.showMessage],
  );

  useApiErrorHandler({
    error: updateError as Error,
    onError: (err) => {
      if (err.status === 409) {
        props.showMessage(
          'Sorry, this email address is already taken. Please try with a different email.',
          MessageType.Failure,
        );
      } else {
        props.showMessage('An error occurred. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('An error occurred. Please try again.', MessageType.Failure),
  });

  return (
    <DashboardRegisterConfirmView
      firstName={props?.onboardingFlow?.attributes?.['name-first'] ?? ''}
      email={props?.onboardingFlow?.attributes?.['email-address'] ?? ''}
      resendEmail={resendEmail}
      updateEmail={updateEmail}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    onboardingFlow: state.onboardingFlow.onboardingFlow,
  };
};

const mapDispatchToProps = {
  saveOnboardingFlow,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardRegisterConfirmRoute);
